import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  RadioGroup,
  Button,
  Alert,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import type { TaxRegimePresentation } from '@bbpos/ui';
import { useSendInvoice, useBillReceipt } from '@/features/communications';
import type { BillReceiptData } from '@/features/communications';
import { useCustomerPurchases } from '@/features/customers';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { formatPaise } from '@/utils/currency';
import type { CurrencyCode } from '@bbpos/types';

// Currency-aware money formatter — delegates to the shared, subunit-aware
// `@/utils/currency#formatPaise`. The INR path is byte-identical to the
// previous inline rupee/100 toLocaleString (min:2, max:2).
function fmtPaise(p: number, currency: CurrencyCode): string {
  return formatPaise(p, currency);
}

function ReceiptCard({
  receipt,
  currency,
  tax,
}: {
  receipt: BillReceiptData;
  currency: CurrencyCode;
  tax: TaxRegimePresentation;
}) {
  return (
    <div className="rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg overflow-hidden">
      {/* Header with shop branding */}
      <div className="bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-5">
        <div className="flex items-center gap-3">
          {receipt.shop_logo ? (
            <img src={receipt.shop_logo} alt="" className="h-10 w-10 rounded-lg object-cover bg-white/20" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-lg">
              {receipt.shop_name.charAt(0)}
            </div>
          )}
          <div>
            <p className="text-white font-bold text-lg">{receipt.shop_name}</p>
            <p className="text-white/70 text-xs">{receipt.shop_address}</p>
          </div>
        </div>
        {receipt.shop_gstin && tax.taxIdLabel ? (
          <p className="mt-2 text-white/60 text-[10px] font-mono tabular-nums">{tax.taxIdLabel}: {receipt.shop_gstin}</p>
        ) : null}
      </div>

      {/* Bill info */}
      <div className="px-6 py-4 bg-gradient-to-b from-indigo-50/50 to-transparent dark:from-indigo-900/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Invoice</p>
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">{receipt.bill_number}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Date</p>
            <p className="text-sm text-neutral-700 dark:text-neutral-200 font-mono tabular-nums">
              {new Date(receipt.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
        {receipt.customer_name ? (
          <div className="mt-3">
            <p className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Customer</p>
            <p className="text-sm text-neutral-900 dark:text-neutral-100">{receipt.customer_name}</p>
          </div>
        ) : null}
      </div>

      {/* Items */}
      <div className="px-6 py-3">
        <table className="w-full text-sm" aria-label="Invoice items">
          <thead>
            <tr className="text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
              <th className="text-left pb-2">Item</th>
              <th className="text-right pb-2 w-12">Qty</th>
              <th className="text-right pb-2 w-20">Rate</th>
              <th className="text-right pb-2 w-16">{tax.taxName}</th>
              <th className="text-right pb-2 w-24">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/50">
            {receipt.items.map((item, i) => (
              <tr key={i} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/30 transition-colors">
                <td className="py-2.5 text-neutral-900 dark:text-neutral-100 font-medium">{item.name}</td>
                <td className="py-2.5 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-200">{item.quantity}</td>
                <td className="py-2.5 text-right font-mono tabular-nums text-neutral-700 dark:text-neutral-200">{fmtPaise(item.unit_price, currency)}</td>
                <td className="py-2.5 text-right font-mono tabular-nums text-neutral-500 dark:text-neutral-400 text-xs">{item.gst_rate}%</td>
                <td className="py-2.5 text-right font-mono tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">{fmtPaise(item.total, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-6 py-4 bg-gradient-to-b from-transparent to-neutral-50 dark:to-neutral-800/50">
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-neutral-500 dark:text-neutral-400">Subtotal</span>
            <span className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{fmtPaise(receipt.subtotal, currency)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-500 dark:text-neutral-400">{tax.taxName}</span>
            <span className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{fmtPaise(receipt.total_gst, currency)}</span>
          </div>
          {receipt.discount > 0 ? (
            <div className="flex justify-between">
              <span className="text-success-600 dark:text-success-400">Discount</span>
              <span className="font-mono tabular-nums text-success-600 dark:text-success-400">-{fmtPaise(receipt.discount, currency)}</span>
            </div>
          ) : null}
          {(receipt.rounding_adjustment ?? 0) !== 0 ? (
            <div className="flex justify-between">
              <span className="text-neutral-500 dark:text-neutral-400">Rounding</span>
              <span className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                {(receipt.rounding_adjustment ?? 0) < 0 ? '-' : '+'}{fmtPaise(Math.abs(receipt.rounding_adjustment ?? 0), currency)}
              </span>
            </div>
          ) : null}
          <div className="flex justify-between pt-2 border-t border-neutral-200 dark:border-neutral-700">
            <span className="font-bold text-neutral-900 dark:text-neutral-100">Grand Total</span>
            <span className="font-bold font-mono tabular-nums text-lg text-neutral-900 dark:text-neutral-100">{fmtPaise(receipt.amount_payable ?? receipt.grand_total, currency)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-neutral-500 dark:text-neutral-400">Payment</span>
            <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 uppercase tracking-wider">
              {receipt.payment_method}
            </span>
          </div>
        </div>
      </div>

      {/* Footer links */}
      {receipt.reorder_link ? (
        <div className="px-6 py-3 bg-neutral-50 dark:bg-neutral-800/30 text-center">
          <p className="text-[10px] text-neutral-400">Re-order link included in message</p>
        </div>
      ) : null}
    </div>
  );
}

// Print stylesheet (injected once) — scope the print / print-to-PDF output to
// just the invoice card. Without this, window.print() captures the whole admin
// shell (sidebar nav, page hero, the Send-Invoice form, toasts). Follows the
// pattern used by the Festive ProductionPlanningPage.
const PRINT_STYLE_ID = 'invoice-preview-print';

function ensureInvoicePrintStyles() {
  if (typeof document === 'undefined' || document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      body * { visibility: hidden; }
      #invoice-print-area, #invoice-print-area * { visibility: visible; }
      #invoice-print-area { position: absolute; left: 0; top: 0; width: 100%; }
      .no-print { display: none !important; }
    }
  `;
  document.head.appendChild(style);
}

function InvoicePreviewContent() {
  const dial = useTenantDial();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const [searchParams] = useSearchParams();

  const [billId, setBillId] = useState(searchParams.get('bill') ?? '');
  const [channel, setChannel] = useState<'EMAIL' | 'WHATSAPP'>('WHATSAPP');
  const [recipient, setRecipient] = useState('');
  const [billNotFound, setBillNotFound] = useState(false);

  // Customer profile → Send Invoice links here with ?customer=<id>. Prefill the
  // customer's most recent completed bill (bills list is newest first) when no
  // explicit ?bill= was given; the receipt effect below then fills the recipient.
  const customerId = searchParams.get('bill') ? undefined : searchParams.get('customer') ?? undefined;
  const customerPurchasesQ = useCustomerPurchases(customerId, 1);
  const prefilledFromCustomer = useRef(false);
  useEffect(() => {
    if (prefilledFromCustomer.current || !customerPurchasesQ.data) return;
    prefilledFromCustomer.current = true;
    const latest = customerPurchasesQ.data.data.find((b) => b.status === 'COMPLETED');
    if (latest) setBillId((current) => current || latest.id);
  }, [customerPurchasesQ.data]);

  const { data: receipt, isLoading: receiptLoading, error: receiptError } = useBillReceipt(billId.trim() || undefined);
  const sendMutation = useSendInvoice();

  // Auto-fill recipient from receipt data
  useEffect(() => {
    if (!receipt) return;
    if (channel === 'WHATSAPP' && receipt.customer_phone && !recipient) {
      setRecipient(receipt.customer_phone);
    } else if (channel === 'EMAIL' && receipt.customer_email && !recipient) {
      setRecipient(receipt.customer_email);
    }
  }, [receipt, channel]);

  // Detect bill not found
  useEffect(() => {
    if (receiptError) {
      const msg = (receiptError as Error).message ?? '';
      setBillNotFound(msg.includes('not found') || msg.includes('404'));
    } else {
      setBillNotFound(false);
    }
  }, [receiptError]);

  // Keyboard: Ctrl+Enter → send (only when a valid receipt is actually loaded)
  const canSendRef = useRef(false);
  canSendRef.current = Boolean(receipt) && billId.trim().length > 0 && recipient.trim().length > 0;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSendRef.current) {
        e.preventDefault();
        handleSend();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSend = useCallback(() => {
    if (!billId.trim()) {
      toast({ variant: 'warning', title: 'Bill ID required' });
      return;
    }
    if (!receipt) {
      toast({ variant: 'warning', title: 'No invoice loaded', description: 'Load a valid bill before sending.' });
      return;
    }
    if (!recipient.trim()) {
      toast({ variant: 'warning', title: 'Recipient required' });
      return;
    }
    if (channel === 'EMAIL' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      toast({ variant: 'warning', title: 'Invalid email', description: 'Enter a valid email address.' });
      return;
    }
    const recipientValue = channel === 'WHATSAPP' ? recipient : recipient.trim();
    if (channel === 'WHATSAPP' && !/^\+[1-9]\d{6,14}$/.test(recipientValue)) {
      toast({ variant: 'warning', title: 'Invalid phone', description: 'Enter a valid phone number.' });
      return;
    }

    sendMutation.mutate(
      { bill_id: billId.trim(), channel, recipient: recipientValue },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Invoice queued', description: `Sending via ${channel}` });
        },
        onError: (err) => {
          toast({ variant: 'error', title: 'Failed to send invoice', description: (err as Error).message || 'Please try again.' });
        },
      },
    );
  }, [billId, channel, recipient, receipt, sendMutation, toast]);

  function handlePrint() {
    ensureInvoicePrintStyles();
    window.print();
  }

  function handleDownloadPdf() {
    if (!receipt) return;
    ensureInvoicePrintStyles();
    // No bundled PDF engine, so route through the browser's print-to-PDF — but
    // set a meaningful document title so the saved file defaults to
    // "Invoice-<bill>.pdf" rather than the dashboard page title. This is what
    // distinguishes Download PDF from Print (which keeps the current title).
    const previousTitle = document.title;
    document.title = `Invoice-${receipt.bill_number}`;
    window.print();
    document.title = previousTitle;
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-violet-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Digital Invoice
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Invoice Preview & Send</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Deliver a digital invoice to your customer via WhatsApp or Email.
          </p>
        </div>
      </div>

      {/* Bill not found alert */}
      {billNotFound ? (
        <Alert variant="danger" title="Bill not found" description="Please check the Bill ID and try again." className="mb-6" />
      ) : null}

      {customerId && customerPurchasesQ.data && !billId.trim() ? (
        <Alert variant="info" title="No completed bills" description="This customer has no completed bill to send yet. Enter a Bill ID manually." className="mb-6" />
      ) : null}

      {/* Missing contact warning */}
      {receipt && !receipt.customer_phone && !receipt.customer_email ? (
        <Alert variant="warning" title="No contact info" description="This customer has no phone or email on file. Enter recipient details manually below." className="mb-6" />
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-5 border-l-4 border-indigo-500 pl-3">
            Send Invoice
          </h2>

          <div className="space-y-5">
            <Input
              label="Bill ID"
              value={billId}
              onChange={setBillId}
              placeholder="Paste bill UUID or bill number"
              required
            />

            <RadioGroup
              label="Channel"
              value={channel}
              onChange={(v) => { setChannel(v as 'EMAIL' | 'WHATSAPP'); setRecipient(''); }}
              variant="horizontal"
              options={[
                { value: 'WHATSAPP', label: 'WhatsApp' },
                { value: 'EMAIL', label: 'Email' },
              ]}
            />

            {channel === 'WHATSAPP' ? (
              <Input
                variant="phone"
                defaultDial={dial}
                label="Phone Number"
                value={recipient}
                onChange={setRecipient}
                placeholder="98765 43210"
                required
              />
            ) : (
              <Input
                label="Email Address"
                value={recipient}
                onChange={setRecipient}
                placeholder="customer@example.com"
                required
              />
            )}

            {/* Actions — sticky on mobile */}
            <div className="flex flex-wrap items-center gap-3 pt-2 sm:static fixed bottom-0 left-0 right-0 sm:p-0 p-4 bg-white dark:bg-neutral-800 sm:bg-transparent sm:shadow-none shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.15)] z-10">
              <Button
                variant="primary"
                onClick={handleSend}
                loading={sendMutation.isPending}
                disabled={!receipt}
              >
                Send Invoice
              </Button>
              <Button variant="secondary" onClick={handleDownloadPdf} disabled={!receipt}>
                Download PDF
              </Button>
              <Button variant="secondary" onClick={handlePrint} disabled={!receipt}>
                Print
              </Button>
              <span className="text-[11px] text-neutral-400 hidden sm:inline">Ctrl+Enter</span>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          {receiptLoading && billId.trim() ? (
            <div className="space-y-3">
              <Skeleton variant="rect" height={120} className="rounded-xl" />
              <Skeleton variant="rect" height={200} className="rounded-xl" />
              <Skeleton variant="rect" height={80} className="rounded-xl" />
            </div>
          ) : receipt ? (
            <div id="invoice-print-area">
              <ReceiptCard receipt={receipt} currency={currency} tax={tax} />
            </div>
          ) : (
            <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10">
              <EmptyState
                variant="page"
                title="Select a bill to preview"
                description="Enter a bill ID to see the formatted invoice before sending."
                icon={
                  <svg className="h-20 w-20 text-indigo-200 dark:text-indigo-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function InvoicePreviewPage() {
  return (
    <PageContainer
      title="Invoice Preview"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Invoice Preview' },
      ]}
    >
      <ErrorBoundary resetKey="comm-invoice">
        <InvoicePreviewContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
